/**
 * ISO 3166-1 alpha-2 countries that PatronEZ recognises for accounts +
 * contacts + assignment rules. Flag emoji is derived from the country
 * code via regional indicator symbols — no image asset needed.
 *
 * Curated to the markets PatronEZ targets: APAC primary, EMEA + Americas
 * secondary. Roughly 60 entries — comfortable for a searchable dropdown
 * without showing every micronation in the UN list.
 */

export interface CountryMeta {
  code: string;  // ISO 3166-1 alpha-2, uppercase
  name: string;  // English short name
  aliases?: string[];
}

export const COUNTRIES: CountryMeta[] = [
  // APAC — primary market
  { code: 'JP', name: 'Japan',           aliases: ['nihon', '日本', 'nippon'] },
  { code: 'KR', name: 'South Korea',     aliases: ['korea', 'rok', '한국'] },
  { code: 'CN', name: 'China',           aliases: ['cn', 'prc', '中国'] },
  { code: 'TW', name: 'Taiwan',          aliases: ['roc', '台灣', '台湾'] },
  { code: 'HK', name: 'Hong Kong',       aliases: ['hk', '香港'] },
  { code: 'SG', name: 'Singapore',       aliases: ['sg'] },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'PH', name: 'Philippines' },
  { code: 'IN', name: 'India' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'LK', name: 'Sri Lanka' },

  // Middle East
  { code: 'AE', name: 'United Arab Emirates', aliases: ['uae', 'emirates'] },
  { code: 'SA', name: 'Saudi Arabia',         aliases: ['ksa'] },
  { code: 'IL', name: 'Israel' },
  { code: 'TR', name: 'Turkey' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'OM', name: 'Oman' },

  // Europe
  { code: 'GB', name: 'United Kingdom',  aliases: ['uk', 'britain', 'england'] },
  { code: 'IE', name: 'Ireland' },
  { code: 'DE', name: 'Germany',          aliases: ['deutschland'] },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain',            aliases: ['españa'] },
  { code: 'IT', name: 'Italy' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Netherlands',      aliases: ['holland'] },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia',          aliases: ['czech republic'] },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'GR', name: 'Greece' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },

  // Americas
  { code: 'US', name: 'United States',    aliases: ['usa', 'america'] },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico',           aliases: ['méxico'] },
  { code: 'BR', name: 'Brazil',           aliases: ['brasil'] },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },

  // Africa
  { code: 'ZA', name: 'South Africa',     aliases: ['rsa'] },
  { code: 'EG', name: 'Egypt' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'MA', name: 'Morocco' },
  // --- additional entries to bring the catalogue to the full ~92 ---
  { code: 'BG', name: "Bulgaria" },
  { code: 'KH', name: "Cambodia" },
  { code: 'CR', name: "Costa Rica" },
  { code: 'HR', name: "Croatia" },
  { code: 'CY', name: "Cyprus" },
  { code: 'DO', name: "Dominican Republic" },
  { code: 'EC', name: "Ecuador" },
  { code: 'EE', name: "Estonia" },
  { code: 'GE', name: "Georgia" },
  { code: 'GH', name: "Ghana" },
  { code: 'GT', name: "Guatemala" },
  { code: 'IS', name: "Iceland" },
  { code: 'JO', name: "Jordan" },
  { code: 'KZ', name: "Kazakhstan" },
  { code: 'LV', name: "Latvia" },
  { code: 'LB', name: "Lebanon" },
  { code: 'LT', name: "Lithuania" },
  { code: 'LU', name: "Luxembourg" },
  { code: 'MO', name: "Macao" },
  { code: 'MT', name: "Malta" },
  { code: 'PA', name: "Panama" },
  { code: 'PY', name: "Paraguay" },
  { code: 'RS', name: "Serbia" },
  { code: 'SK', name: "Slovakia" },
  { code: 'SI', name: "Slovenia" },
  { code: 'TZ', name: "Tanzania" },
  { code: 'UY', name: "Uruguay" },
  { code: 'UZ', name: "Uzbekistan" },
];

/**
 * Convert an ISO 3166-1 alpha-2 country code into the regional indicator
 * symbol pair that renders as the country's flag emoji.
 */
export function flagEmoji(code: string): string {
  if (!code || code.length !== 2) return '';
  const A = 0x1f1e6; // Regional Indicator Symbol Letter A
  const base = 'A'.charCodeAt(0);
  const cc = code.toUpperCase();
  return String.fromCodePoint(A + (cc.charCodeAt(0) - base), A + (cc.charCodeAt(1) - base));
}

export function countryByCode(code: string): CountryMeta | undefined {
  if (!code) return undefined;
  const c = code.toUpperCase();
  return COUNTRIES.find((x) => x.code === c);
}
